/**
 * @file BreadcrumbComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Breadcrumb
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-02-26
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { capitalize } from "@/lib/text";
import { getNavigablePathsAction } from "@/features/routing/actions";
import { ROUTE_REGISTRY } from "@/features/routing/route-registry.generated";
import type { SidebarPathname } from "@/features/routing/types";
import { Link } from "@/features/i18n";
import { isDynamicSegment, isPathNavigable } from "@/lib/routing";
import { useSafeTranslation } from "@/hooks/useSafeTranslation";
interface ReplaceCharacter {
  from: string;
  to: string;
}
const replaceCharacterList: ReplaceCharacter[] = [{
  from: "-",
  to: " "
}, {
  from: "_",
  to: " "
}];
const transformLabel = (title: string): string => capitalize(title);
const LOCALES = ["es", "en"] as const;
const SIDEBAR_PATH_SET = new Set<SidebarPathname>(ROUTE_REGISTRY.filter(route => !route.path.includes("[")).map(route => route.path as SidebarPathname));
const isSidebarPathname = (path: string): path is SidebarPathname => SIDEBAR_PATH_SET.has(path as SidebarPathname);
interface BreadcrumbItem {
  label: string;
  href: SidebarPathname | null;
  isNavigable: boolean;
}
const MyBreadcrumbs = () => {
  const $ = _c(19);
  const safeTRoutes = useSafeTranslation("Routes");
  const ariaLabels = useTranslations("AriaLabel");
  const pathname = usePathname();
  const [navigableSet, setNavigableSet] = useState(null);
  const t0 = pathname || "/";
  let segments;
  if ($[0] !== t0) {
    segments = t0.split("/").filter(_temp);
    if (segments.length > 0 && LOCALES.includes(segments[0] as (typeof LOCALES)[number])) {
      segments = segments.slice(1);
    }
    $[0] = t0;
    $[1] = segments;
  } else {
    segments = $[1];
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      (async () => {
        const paths = await getNavigablePathsAction();
        setNavigableSet(new Set(paths));
      })();
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  segments;
  let t3;
  if ($[4] !== navigableSet || $[5] !== safeTRoutes || $[6] !== segments) {
    t3 = segments.map((seg_0, idx) => {
      const href = `/${segments.slice(0, idx + 1).join("/")}`;
      const isNavigable = isPathNavigable(href, navigableSet);
      const isDynamic = isDynamicSegment(idx, segments, navigableSet);
      const decodedSeg = decodeURIComponent(seg_0);
      const staticOrTransformed = transformLabel(replaceCharacterList.reduce(_temp2, decodedSeg));
      let label;
      if (isDynamic || !navigableSet) {
        label = staticOrTransformed;
      } else {
        const translated = safeTRoutes(href);
        label = translated ?? staticOrTransformed;
      }
      const typedHref = isNavigable && isSidebarPathname(href) ? href : null;
      return {
        label,
        href: typedHref,
        isNavigable
      };
    });
    $[4] = navigableSet;
    $[5] = safeTRoutes;
    $[6] = segments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const items = t3;
  if (!navigableSet) {
    return null;
  }
  let t4;
  if ($[8] !== ariaLabels) {
    t4 = ariaLabels("breadcrumb");
    $[8] = ariaLabels;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== items) {
    let t6;
    if ($[12] !== items.length) {
      t6 = (item, index) => {
        const isActive = index === items.length - 1;
        return <li key={`bc-${index}-${item.href ?? ""}-${item.label}`} className="breadcrumb__item" aria-current={isActive ? "page" : undefined}>{isActive || !item.isNavigable || !item.href ? <span>{item.label}</span> : <Link href={item.href}>{item.label}</Link>}</li>;
      };
      $[12] = items.length;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = items.map(t6);
    $[10] = items;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <ol>{t5}</ol>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <nav aria-label={t4} className="breadcrumb">{t6}</nav>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
export default MyBreadcrumbs;
function _temp(seg) {
  return seg.length > 0;
}
function _temp2(acc, r) {
  return acc.split(r.from).join(r.to);
}